import { InfoCircleOutlined } from '@ant-design/icons';
import { Radio, Tooltip } from 'antd';
import DownloadIcon from 'assets/svg/DownloadIcon';
import AppBreadcrumb from 'components/molecules/common/AppBreadcrumb';
import { colorConstants } from 'config/colorConstants';
import {
    PPA_WHITESPACE_BACK_BTN,
    PPA_WHITESPACE_EXPORT_BTN,
    PPA_WHITESPACE_INFO_BTN,
    PPA_WHITESPACE_TITLE,
} from 'constants/ppaTestIds';
import { memo, type ReactNode } from 'react';

export const WhitespaceAnalysisBreadcrumb = memo(({
    title,
    groupLabel = 'Opportunity Assessment',
}: {
    title: string;
    groupLabel?: string;
}) => (
    <AppBreadcrumb items={[{ label: groupLabel }, { label: title }]} showHomeIcon={false} />
));

export const WhitespaceAnalysisPageHeader = memo(({
    title,
    onBack,
    tooltipContent,
    onDownloadClick,
    downloadDisabled,
}: {
    title: string;
    onBack: () => void;
    tooltipContent: ReactNode;
    onDownloadClick: () => void;
    downloadDisabled: boolean;
}) => (
    <div className="flex justify-between items-center w-full pb-3">
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-backgroundGreyAlt transition-colors"
                aria-label="Go back"
                data-testid={PPA_WHITESPACE_BACK_BTN}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L6.41421 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H6.41421L10.7071 17.2929C11.0976 17.6834 11.0976 18.3166 10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z"
                        fill={colorConstants.headerColor}
                    />
                </svg>
            </button>
            <h1
                className="m-0 text-[20px] font-semibold leading-8 tracking-[0.5px] text-fontColor"
                data-testid={PPA_WHITESPACE_TITLE}
            >
                {title}
            </h1>
            <Tooltip
                placement="right"
                color={colorConstants.blackTransparent88}
                overlayInnerStyle={{ width: 'auto', maxWidth: '720px' }}
                title={tooltipContent}
            >
                <InfoCircleOutlined className="cursor-pointer opacity-50" data-testid={PPA_WHITESPACE_INFO_BTN} />
            </Tooltip>
        </div>
        <button
            type="button"
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-borderGreyAl1 
                bg-transparent p-0 text-toolbarLabelGrey shadow-none hover:border-purpleFontSelected 
                hover:bg-transparent hover:text-toolbarLabelGrey active:border-purpleFontPressed 
                disabled:cursor-not-allowed disabled:text-toolbarLabelGrey ${downloadDisabled ? '!border-purpleFontPressed' : ''}`
            }
            aria-label="Download"
            disabled={downloadDisabled}
            onClick={onDownloadClick}
            data-testid={PPA_WHITESPACE_EXPORT_BTN}
        >
            <DownloadIcon stroke="currentColor" width={14} height={14} />
        </button>
    </div>
));

export const WhitespaceAxisRadioGroup = <T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: readonly T[];
    onChange: (nextValue: T) => void;
}) => (
    <div className="w-[180px]">
        <p className="mb-2 text-[14px] font-semibold leading-[22px]" style={{ color: colorConstants.axisLabelGrey }}>{label}</p>
        <Radio.Group value={value} onChange={(event) => onChange(event.target.value as T)} className="flex flex-col gap-2">
            {options.map((option) => (
                <Radio key={option} value={option} className="!m-0">
                    <span className="rounded-md px-1 py-0.5" style={{ backgroundColor: value === option ? colorConstants.axisSelectedBg : 'transparent' }}>
                        {option}
                    </span>
                </Radio>
            ))}
        </Radio.Group>
    </div>
);
